/**
 * Tab Coordinator - Manages leader election and cross-tab coordination
 * 
 * Implements leader-follower pattern to prevent duplicate:
 * - WebSocket connections
 * - API polling
 * - Bot executions
 * 
 * Leader: Runs expensive services (WebSockets, polling, bots)
 * Followers: Receive data via BroadcastChannel, display UI only
 */

export class TabCoordinator {
    constructor() {
        this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        this.mode = 'initializing'; // 'initializing', 'leader', 'follower'
        this.leaderTabId = null;
        this.lastHeartbeat = null;
        
        this.leadershipChannel = null;
        this.heartbeatInterval = null;
        this.heartbeatMonitorInterval = null;
        
        // Service references (set by initialize())
        this.services = {
            positionTracker: null,
            priceService: null,
            chaseTracker: null,
            autoTradeBot: null,
            defenseBot: null,
            trailingSLBot: null,
            cancellationBot: null,
            positionMonitor: null,
            pairsAutoCloseBot: null
        };
        
        this.userAddress = null;
    }
    
    /**
     * Initialize tab coordinator and start leader election
     */
    async initialize() {
        window.tabId = this.tabId;
        // console.log(`[TAB-COORDINATOR] Initializing tab ${this.tabId}`);
        
        // Setup leadership channel
        this.leadershipChannel = new BroadcastChannel('atv4_leadership');
        this.leadershipChannel.onmessage = (e) => this.handleLeadershipMessage(e.data);
        
        // Start election
        await this.startElection();
        
        // Setup heartbeat monitor (followers only)
        if (this.mode === 'follower') {
            this.startHeartbeatMonitor();
        }
    }
    
    /**
     * Start leader election process
     */
    async startElection() {
        // console.log(`[TAB-COORDINATOR] Starting leader election...`);
        
        // Broadcast ping
        this.leadershipChannel.postMessage({
            type: 'LEADER_PING',
            tabId: this.tabId,
            timestamp: Date.now()
        });
        
        // Wait 1 second for response
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // No leader responded, become leader
        if (!this.leaderTabId) {
            // console.log(`[TAB-COORDINATOR] No leader found, becoming leader`);
            await this.becomeLeader();
        } else {
            // console.log(`[TAB-COORDINATOR] Leader exists: ${this.leaderTabId}, becoming follower`);
        }
    }
    
    /**
     * Become the leader tab
     */
    async becomeLeader() {
        this.mode = 'leader';
        this.leaderTabId = this.tabId;
        
        // console.log(`[TAB-COORDINATOR] 👑 Became LEADER`);
        
        // Announce leadership
        this.leadershipChannel.postMessage({
            type: 'LEADER_ANNOUNCE',
            tabId: this.tabId,
            timestamp: Date.now()
        });
        
        // Start services in leader mode
        await this.startServicesAsLeader();
        
        // Start heartbeat
        this.startHeartbeat();
    }
    
    /**
     * Become a follower tab
     */
    async becomeFollower(leaderTabId) {
        this.mode = 'follower';
        this.leaderTabId = leaderTabId;
        
        // console.log(`[TAB-COORDINATOR] 📡 Became FOLLOWER (leader: ${leaderTabId})`);
        
        // Start services in follower mode
        await this.startServicesAsFollower();
        
        // Start heartbeat monitoring
        this.startHeartbeatMonitor();
    }
    
    /**
     * Start all services in leader mode
     */
    async startServicesAsLeader() {
        const { positionTracker, priceService, chaseTracker, autoTradeBot, defenseBot, 
                trailingSLBot, cancellationBot, positionMonitor, pairsAutoCloseBot } = this.services;
        
        // console.log(`[TAB-COORDINATOR] Starting services as LEADER...`);
        
        // Core data services (leader mode)
        if (positionTracker && this.userAddress) {
            await positionTracker.start(this.userAddress, 'leader');
        }
        if (priceService) {
            await priceService.start('leader');
        }
        if (chaseTracker) {
            await chaseTracker.start('leader');
        }
        
        // Bots (leader only) - check if they were enabled before
        if (autoTradeBot?.enabled) {
            await autoTradeBot.setMode('leader');
        }
        if (defenseBot?.isEnabled) {
            await defenseBot.setMode('leader');
        }
        if (trailingSLBot?.isEnabled) {
            await trailingSLBot.setMode('leader');
        }
        if (cancellationBot?.enabled) {
            await cancellationBot.setMode('leader');
        }
        if (positionMonitor?.isRunning) {
            await positionMonitor.setMode('leader');
        }
        if (pairsAutoCloseBot?.isRunning) {
            await pairsAutoCloseBot.setMode('leader');
        }
        
        // console.log(`[TAB-COORDINATOR] ✅ All services started as LEADER`);
    }
    
    /**
     * Start services in follower mode
     */
    async startServicesAsFollower() {
        const { positionTracker, priceService, chaseTracker, autoTradeBot, defenseBot,
                trailingSLBot, cancellationBot, positionMonitor, pairsAutoCloseBot } = this.services;
        
        console.log(`[TAB-COORDINATOR] Starting services as FOLLOWER...`);
        
        // Core data services (follower mode - listen to broadcasts)
        if (positionTracker && this.userAddress) {
            await positionTracker.start(this.userAddress, 'follower');
        }
        if (priceService) {
            await priceService.start('follower');
        }
        if (chaseTracker) {
            await chaseTracker.start('follower');
        }
        
        // Bots disabled in follower mode
        if (autoTradeBot) await autoTradeBot.setMode('follower');
        if (defenseBot) await defenseBot.setMode('follower');
        if (trailingSLBot) await trailingSLBot.setMode('follower');
        if (cancellationBot) await cancellationBot.setMode('follower');
        if (positionMonitor) await positionMonitor.setMode('follower');
        if (pairsAutoCloseBot) await pairsAutoCloseBot.setMode('follower');
        
        console.log(`[TAB-COORDINATOR] ✅ All services started as FOLLOWER`);
    }
    
    /**
     * Start sending heartbeat (leader only)
     */
    startHeartbeat() {
        if (this.heartbeatInterval) return;
        
        this.heartbeatInterval = setInterval(() => {
            this.leadershipChannel.postMessage({
                type: 'HEARTBEAT',
                tabId: this.tabId,
                timestamp: Date.now()
            });
        }, 5000); // Every 5 seconds
    }
    
    /**
     * Start monitoring leader heartbeat (followers only)
     */
    startHeartbeatMonitor() {
        if (this.mode === 'leader') return;
        if (this.heartbeatMonitorInterval) return;
        
        this.heartbeatMonitorInterval = setInterval(() => {
            if (!this.lastHeartbeat) return;
            
            const age = Date.now() - this.lastHeartbeat;
            if (age > 15000) { // 15 seconds timeout
                console.warn(`[TAB-COORDINATOR] Leader heartbeat timeout, triggering re-election`);
                this.handleLeaderDeath();
            }
        }, 5000);
    }
    
    /**
     * Handle leadership messages from other tabs
     */
    handleLeadershipMessage(msg) {
        // Ignore our own messages
        if (msg.tabId === this.tabId) return;
        
        switch(msg.type) {
            case 'LEADER_PING':
                if (this.mode === 'leader') {
                    // Respond as leader
                    this.leadershipChannel.postMessage({
                        type: 'LEADER_PONG',
                        tabId: this.tabId,
                        timestamp: Date.now(),
                        isLeader: true
                    });
                }
                break;
                
            case 'LEADER_PONG':
                if (msg.isLeader && this.mode === 'initializing') {
                    this.becomeFollower(msg.tabId);
                }
                break;
                
            case 'LEADER_ANNOUNCE':
                if (this.mode === 'initializing' || (this.mode === 'follower' && msg.tabId !== this.leaderTabId)) {
                    this.becomeFollower(msg.tabId);
                }
                break;
                
            case 'HEARTBEAT':
                if (msg.tabId === this.leaderTabId) {
                    this.lastHeartbeat = msg.timestamp;
                }
                break;
                
            case 'LEADER_SHUTDOWN':
                if (msg.tabId === this.leaderTabId) {
                    console.log(`[TAB-COORDINATOR] Leader shutting down, triggering re-election`);
                    this.handleLeaderDeath();
                }
                break;
        }
    }
    
    /**
     * Handle leader death - trigger re-election
     */
    async handleLeaderDeath() {
        // Stop heartbeat monitor
        if (this.heartbeatMonitorInterval) {
            clearInterval(this.heartbeatMonitorInterval);
            this.heartbeatMonitorInterval = null;
        }
        
        // Reset state
        this.leaderTabId = null;
        this.lastHeartbeat = null;
        this.mode = 'initializing';
        
        // Trigger re-election
        await this.startElection();
    }
    
    /**
     * Promote follower to leader (manual or after leader death)
     */
    async promoteToLeader() {
        if (this.mode === 'leader') return;
        
        console.log(`[TAB-COORDINATOR] Promoting to leader...`);
        
        // Stop follower mode services
        await this.stopAllServices();
        
        // Become leader
        await this.becomeLeader();
    }
    
    /**
     * Stop all services (for mode switching)
     */
    async stopAllServices() {
        const { positionTracker, priceService } = this.services;
        
        if (positionTracker?.isRunning) {
            await positionTracker.stop();
        }
        if (priceService?.isConnected) {
            await priceService.stop();
        }
        
        // Bots stop automatically when mode changes
    }
    
    /**
     * Shutdown coordinator (tab closing)
     */
    async shutdown() {
        console.log(`[TAB-COORDINATOR] Shutting down tab ${this.tabId}`);
        
        if (this.mode === 'leader') {
            // Announce shutdown to trigger re-election
            this.leadershipChannel.postMessage({
                type: 'LEADER_SHUTDOWN',
                tabId: this.tabId,
                timestamp: Date.now()
            });
        }
        
        // Stop heartbeat
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }
        
        // Stop heartbeat monitor
        if (this.heartbeatMonitorInterval) {
            clearInterval(this.heartbeatMonitorInterval);
        }
        
        // Close channels
        if (this.leadershipChannel) {
            this.leadershipChannel.close();
        }
    }
    
    /**
     * Get current status
     */
    getStatus() {
        return {
            tabId: this.tabId,
            mode: this.mode,
            leaderTabId: this.leaderTabId,
            lastHeartbeat: this.lastHeartbeat,
            isLeader: this.mode === 'leader'
        };
    }
}

// Create singleton
if (typeof window !== 'undefined') {
    window.TabCoordinator = new TabCoordinator();
}

export default TabCoordinator;
